import api from '../api/client';
// components/AnimalMortalityHistory.jsx
import React, { useRef, useState } from 'react';
import {
    Card,
    Tag,
    Space,
    Button,
    Modal,
    Descriptions,
    Typography,
    Divider,
    Tooltip,
    Badge,
    Select,
    DatePicker,
    Input,
    Progress,
    ConfigProvider
} from 'antd';
import { ProTable } from '@ant-design/pro-table';
import {
    EyeOutlined,
    WarningOutlined,
    CalendarOutlined,
    TextOutlined,
    TeamOutlined,
    DollarOutlined,
    FileTextOutlined,
    DownloadOutlined,
    ReloadOutlined,
    FilterOutlined,
    ArrowLeftOutlined
} from '@ant-design/icons';
import axios from 'axios';
import dayjs from 'dayjs';
import config from '../config';
import { Link } from 'react-router-dom/cjs/react-router-dom.min';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const AnimalMortalityHistory = () => {
    const actionRef = useRef();
    const [detailsModal, setDetailsModal] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [filters, setFilters] = useState({
        mortality_type: 'all',
        species: 'all',
        dateRange: null,
        farm: 'all'
    });
    const [summary, setSummary] = useState({
        totalMortality: 0,
        parentMortality: 0,
        offspringMortality: 0,
        totalLossValue: 0,
        bySpecies: {},
        byFarm: {},
        monthlyTrend: []
    });

    const environment = window.location.hostname === 'localhost' ? 'development' : 'production';
    const apiUrl = config[environment].apiUrl;

    // Fetch summary statistics
    const fetchSummary = async () => {
        try {
            const params = {};
            if (filters.dateRange && filters.dateRange.length === 2) {
                params.startDate = filters.dateRange[0].format('YYYY-MM-DD');
                params.endDate = filters.dateRange[1].format('YYYY-MM-DD');
            }
            if (filters.farm !== 'all') params.farm = filters.farm;
            if (filters.species !== 'all') params.species = filters.species;

            const response = await api.get(`/animal-mortality/summary`, { params });
            setSummary(response.data);
        } catch (error) {
            console.error('Error fetching summary:', error);
        }
    };

    // ProTable columns
    const columns = [
        {
            title: 'Date',
            dataIndex: 'eventDate',
            key: 'eventDate',
            width: 110,
            render: (date) => dayjs(date).format('DD MMM YYYY'),
            sorter: (a, b) => dayjs(a.eventDate).unix() - dayjs(b.eventDate).unix()
        },
        {
            title: 'Animal',
            dataIndex: 'tagNumber',
            key: 'tagNumber',
            width: 120,
            render: (text, record) => (
                <Space>
                    {' '}
                    <Text>{text || record.batchId || 'N/A'}</Text>{' '}
                    {record.animalName && (
                        <Text type="secondary" style={{ fontSize: 11 }}>
                            ({record.animalName})
                        </Text>
                    )}{' '}
                </Space>
            )
        },
        {
            title: 'Species',
            dataIndex: 'type_slug',
            key: 'type_slug',
            width: 90,
            render: (slug) => {
                const colors = { goat: 'green', sheep: 'blue', pig: 'orange', poultry: 'gold', weaner: 'purple' };
                return <Text>{slug?.toUpperCase()}</Text>;
            },
            filters: [
                { text: 'Goat', value: 'goat' },
                { text: 'Sheep', value: 'sheep' },
                { text: 'Pig', value: 'pig' },
                { text: 'Poultry', value: 'poultry' },
                { text: 'Weaner', value: 'weaner' }
            ],
            onFilter: (value, record) => record.type_slug === value
        },
        {
            title: 'Mortality Type',
            dataIndex: 'mortalityType',
            key: 'mortalityType',
            width: 110,
            render: (type) => <Text>{type === 'parent' ? 'Adult Mortality' : 'Offspring Mortality'}</Text>,
            filters: [
                { text: 'Adult Mortality', value: 'parent' },
                { text: 'Offspring Mortality', value: 'offspring' }
            ],
            onFilter: (value, record) => record.mortalityType === value
        },
        {
            title: 'Age at Death',
            dataIndex: 'ageAtDeath',
            key: 'ageAtDeath',
            width: 100,
            render: (age) => age || '-'
        },
        // {
        //   title: 'Quantity',
        //   dataIndex: 'mortalityQuantity',
        //   key: 'mortalityQuantity',
        //   width: 100,
        //   render: (quantity) => quantity || '-',
        // },
        {
            title: 'Value Lost (KES)',
            dataIndex: 'valueBefore',
            key: 'valueBefore',
            width: 120,
            align: 'right',
            render: (value) => (value ? `KES ${value.toLocaleString()}` : '-'),
            sorter: (a, b) => (a.valueBefore || 0) - (b.valueBefore || 0)
        },
        {
            title: 'Farm',
            dataIndex: 'farm',
            key: 'farm',
            width: 100
        },
        {
            title: 'Recorded By',
            dataIndex: 'userName',
            key: 'userName',
            width: 100
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 80,
            render: (_, record) => (
                <Tooltip title="View Details">
                    <Button
                        type="link"
                        size="small"
                        icon={<EyeOutlined />}
                        onClick={() => {
                            setSelectedEvent(record);
                            setDetailsModal(true);
                        }}
                    />
                </Tooltip>
            )
        }
    ];

    // Request function for ProTable
    const request = async (params, sort, filter) => {
        try {
            const queryParams = {};

            // Apply filters
            if (filters.mortality_type !== 'all') queryParams.mortalityType = filters.mortality_type;
            if (filters.species !== 'all') queryParams.species = filters.species;
            if (filters.farm !== 'all') queryParams.farm = filters.farm;
            if (filters.dateRange && filters.dateRange.length === 2) {
                queryParams.startDate = filters.dateRange[0].format('YYYY-MM-DD');
                queryParams.endDate = filters.dateRange[1].format('YYYY-MM-DD');
            }

            // Apply search
            if (params.search) queryParams.search = params.search;

            // Apply sorting
            if (sort && Object.keys(sort).length > 0) {
                const sortField = Object.keys(sort)[0];
                queryParams.sort = `${sortField}:${sort[sortField] === 'ascend' ? 'ASC' : 'DESC'}`;
            }

            // Pagination
            queryParams.page = params.current || 1;
            queryParams.limit = params.pageSize || 20;

            const response = await api.get(`/animal-mortality/list`, { params: queryParams });
            await fetchSummary();

            return {
                data: response.data.data,
                success: true,
                total: response.data.pagination?.total || 0
            };
        } catch (error) {
            console.error('Error fetching mortality records:', error);
            return {
                data: [],
                success: false,
                total: 0
            };
        }
    };

    // Export to CSV
    const exportToCSV = async () => {
        try {
            const queryParams = {};
            if (filters.mortality_type !== 'all') queryParams.mortalityType = filters.mortality_type;
            if (filters.species !== 'all') queryParams.species = filters.species;
            if (filters.farm !== 'all') queryParams.farm = filters.farm;
            if (filters.dateRange && filters.dateRange.length === 2) {
                queryParams.startDate = filters.dateRange[0].format('YYYY-MM-DD');
                queryParams.endDate = filters.dateRange[1].format('YYYY-MM-DD');
            }

            const response = await api.get(`/animal-mortality/export`, {
                params: queryParams,
                responseType: 'blob'
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `mortality_report_${dayjs().format('YYYY-MM-DD')}.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Error exporting data:', error);
        }
    };

    // Calculate mortality rate
    const mortalityRate =
        summary.totalMortality > 0
            ? (((summary.parentMortality + summary.offspringMortality) / summary.totalMortality) * 100).toFixed(1)
            : 0;

    return (
        <>
            <Card
                title={
                    <Space>
                        <WarningOutlined style={{ color: '#ff4d4f' }} />
                        <span>Animal Mortality History</span>
                        <Badge count={summary.totalMortality} showZero color="red" />
                    </Space>
                }
                extra={
                    <Space>
                        <Button icon={<ReloadOutlined />} onClick={() => actionRef.current?.reload()}>
                            Refresh
                        </Button>
                        <Button icon={<DownloadOutlined />} onClick={exportToCSV}>
                            Export CSV
                        </Button>
                        <Link to="/animal-assets">
                            <Button type="primary" icon={<ArrowLeftOutlined />}>
                                Back
                            </Button>
                        </Link>
                        {/* <Link to="/animal-assets/mortality/new">
            <Button type="primary" icon={<FileTextOutlined />}>
              Record Mortality
            </Button>
          </Link> */}
                    </Space>
                }
                //   style={{ margin: 16 }}
            >
                {/* Summary Statistics Cards */}
                <div style={{ marginBottom: 16 }}>
                    {/* <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                        <Card size="small" style={{ flex: 1, minWidth: 150 }}>
                            <Statistic
                                title="Total Mortality"
                                value={summary.totalMortality}
                                valueStyle={{ color: '#cf1322', fontSize: 24 }}
                                prefix={<WarningOutlined />}
                            />
                            <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>
                                <span>Adults: {summary.parentMortality}</span>
                                <span style={{ marginLeft: 8 }}>Offspring: {summary.offspringMortality}</span>
                            </div>
                        </Card>

                        <Card size="small" style={{ flex: 1, minWidth: 150 }}>
                            <Statistic
                                title="Total Loss Value"
                                value={summary.totalLossValue || 0}
                                precision={2}
                                prefix="KES"
                                valueStyle={{ color: '#ff4d4f', fontSize: 24 }}
                            />
                        </Card>

                        <Card size="small" style={{ flex: 1, minWidth: 150 }}>
                            <Statistic
                                title="Mortality Rate"
                                value={mortalityRate}
                                suffix="%"
                                valueStyle={{ color: mortalityRate > 10 ? '#cf1322' : '#faad14', fontSize: 24 }}
                            />
                            <Progress
                                percent={Math.min(mortalityRate, 100)}
                                size="small"
                                strokeColor={mortalityRate > 10 ? '#cf1322' : '#faad14'}
                                showInfo={false}
                            />
                        </Card>
                    </div> */}

                    {/* Summary by Species */}
                    {Object.keys(summary.bySpecies || {}).length > 0 && (
                        <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
                            {Object.entries(summary.bySpecies).map(([species, data]) => (
                                <Card key={species} size="small" style={{ flex: 1, minWidth: 120 }}>
                                    <div style={{ textAlign: 'center' }}>
                                        <Text color="blue">{species.toUpperCase()}</Text>
                                        <div style={{ fontSize: 20, fontWeight: 'bold', marginTop: 4 }}>{data.count}</div>
                                        <div style={{ fontSize: 11, color: '#666' }}>Loss: KES {data.value?.toLocaleString()}</div>
                                    </div>
                                </Card>
                            ))}
                        </div>
                    )}
                </div>

                {/* Search and Filter Bar */}
                <div style={{ marginBottom: 16, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                    <Input.Search
                        placeholder="Search by Text number..."
                        onSearch={(value) => {
                            actionRef.current?.setPageInfo({ current: 1 });
                            actionRef.current?.reload();
                        }}
                        onChange={(e) => {
                            if (!e.target.value) {
                                actionRef.current?.reload();
                            }
                        }}
                        style={{ width: 250 }}
                        allowClear
                    />

                    <Select
                        placeholder="Mortality Type"
                        value={filters.mortality_type}
                        onChange={(value) => {
                            setFilters({ ...filters, mortality_type: value });
                            actionRef.current?.reload();
                        }}
                        style={{ width: 150 }}
                        allowClear
                    >
                        <Option value="all">All Types</Option>
                        <Option value="parent">Adult Mortality</Option>
                        <Option value="offspring">Offspring Mortality</Option>
                    </Select>

                    <Select
                        placeholder="Species"
                        value={filters.species}
                        onChange={(value) => {
                            setFilters({ ...filters, species: value });
                            actionRef.current?.reload();
                        }}
                        style={{ width: 120 }}
                        allowClear
                    >
                        <Option value="all">All Species</Option>
                        <Option value="goat">Goat</Option>
                        <Option value="sheep">Sheep</Option>
                        <Option value="pig">Pig</Option>
                        <Option value="poultry">Poultry</Option>
                        <Option value="weaners">Weaners</Option>
                    </Select>

                    <Select
                        placeholder="Farm"
                        value={filters.farm}
                        onChange={(value) => {
                            setFilters({ ...filters, farm: value });
                            actionRef.current?.reload();
                        }}
                        style={{ width: 150 }}
                        allowClear
                    >
                        <Option value="all">All Farms</Option>
                        {/* Add farm options dynamically */}
                    </Select>

                    <RangePicker
                        placeholder={['Start Date', 'End Date']}
                        onChange={(dates) => {
                            setFilters({ ...filters, dateRange: dates });
                            actionRef.current?.reload();
                        }}
                        style={{ width: 250 }}
                    />

                    <Button
                        icon={<FilterOutlined />}
                        onClick={() => {
                            setFilters({
                                mortality_type: 'all',
                                species: 'all',
                                dateRange: null,
                                farm: 'all'
                            });
                            actionRef.current?.reload();
                        }}
                    >
                        Reset Filters
                    </Button>
                </div>

                {/* Mortality Records Table */}
                <ProTable
                    actionRef={actionRef}
                    defaultSize="small"
                    columns={columns}
                    request={request}
                    rowKey="id"
                    search={false}
                    pagination={{
                        defaultPageSize: 20,
                        showSizeChanger: true,
                        showQuickJumper: true
                    }}
                    scroll={{ x: 1000 }}
                    options={{
                        reload: true,
                        density: true,
                        setting: true
                    }}
                />

                {/* Details Modal */}
                <Modal
                    title={
                        <Space>
                            <WarningOutlined style={{ color: '#ff4d4f' }} />
                            <span>Mortality Event Details</span>
                        </Space>
                    }
                    open={detailsModal}
                    onCancel={() => {
                        setDetailsModal(false);
                        setSelectedEvent(null);
                    }}
                    footer={[
                        <Button key="close" onClick={() => setDetailsModal(false)}>
                            Close
                        </Button>
                    ]}
                    width={600}
                >
                    {selectedEvent && (
                        <>
                            <Descriptions bordered column={2} size="small">
                                <Descriptions.Item label="Date" span={2}>
                                    {dayjs(selectedEvent.eventDate).format('DD MMM YYYY, h:mm A')}
                                </Descriptions.Item>
                                <Descriptions.Item label="Animal Text">
                                    <Text color="red">{selectedEvent.TextNumber || selectedEvent.batchId || 'N/A'}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Species">
                                    <Text color="blue">{selectedEvent.type_slug?.toUpperCase()}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Mortality Type" span={2}>
                                    <Text color={selectedEvent.mortalityType === 'parent' ? 'orange' : 'red'}>
                                        {selectedEvent.mortalityType === 'parent' ? 'Adult Mortality' : 'Offspring Mortality'}
                                    </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Age at Death">{selectedEvent.ageAtDeath || 'Not recorded'}</Descriptions.Item>
                                <Descriptions.Item label="Value Lost">
                                    <Text strong style={{ color: '#ff4d4f' }}>
                                        KES {selectedEvent.valueBefore?.toLocaleString() || 0}
                                    </Text>
                                </Descriptions.Item>
                                <Descriptions.Item label="Farm">{selectedEvent.farm || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Recorded By">{selectedEvent.userName || '-'}</Descriptions.Item>
                                <Descriptions.Item label="Remarks" span={2}>
                                    {selectedEvent.remarks || '-'}
                                </Descriptions.Item>
                            </Descriptions>

                            {selectedEvent.eventCount > 1 && (
                                <>
                                    <Divider />
                                    <Title level={5}>Affected Animals</Title>
                                    <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                                        <Text>
                                            Number of animals affected: <strong>{selectedEvent.eventCount}</strong>
                                        </Text>
                                    </div>
                                </>
                            )}

                            {selectedEvent.details && (
                                <>
                                    <Divider />
                                    <Title level={5}>Additional Information</Title>
                                    <div style={{ padding: 12, background: '#f5f5f5', borderRadius: 8 }}>
                                        <pre style={{ margin: 0, fontSize: 12, whiteSpace: 'pre-wrap' }}>
                                            {JSON.stringify(selectedEvent.details, null, 2)}
                                        </pre>
                                    </div>
                                </>
                            )}
                        </>
                    )}
                </Modal>
            </Card>
        </>
    );
};

// Helper Statistic component
const Statistic = ({ title, value, prefix, suffix, valueStyle, precision }) => {
    const formattedValue = precision !== undefined ? value?.toFixed(precision) : value;
    return (
        <div>
            <div style={{ fontSize: 12, color: '#888', marginBottom: 4 }}>{title}</div>
            <div style={{ fontSize: 24, fontWeight: 600, ...valueStyle }}>
                {prefix && <span style={{ marginRight: 4 }}>{prefix}</span>}
                {formattedValue?.toLocaleString()}
                {suffix && <span style={{ marginLeft: 4 }}>{suffix}</span>}
            </div>
        </div>
    );
};

export default AnimalMortalityHistory;
